<script>
    import { createForm } from "svelte-forms-lib";
    import { navigate } from "svelte-routing";
    import { onMount } from 'svelte';
    import InputField from "../InputField.svelte";
    import FormCard from "../FormCard.svelte";
    import * as yup from "yup";
    import {
        createMembershipPrint,
        deleteMembershipPrint,
        exportMPToXLSX,
        fromFormToPayload,
        getMembershipPrint,
        getNextCode,
        updateMembershipPrint
    } from "../../../services/membershipPrintService";
    import DateSearcher from "../../SearchBars/DateSearcher.svelte";
    import TextareaField from "../TextareaField.svelte";
    import { format } from "date-fns";
    import { downloadFile } from "../../../utils/downloadFile";
    import toast from "svelte-french-toast";
    import { Tooltip } from "@svelte-plugins/tooltips";
    import { selectedActiveYear } from "../../../stores/selectedActiveYear";
    import { closeAllModals, openModal } from "svelte-modals";
    import SelectSubscriberModal from "../../Modal/SelectSubscriberModal.svelte";
    import ButtonPromise from "../../Button/ButtonPromise.svelte";

    const urlParams = new URLSearchParams(window.location.search);
    export let membershipPrintId;

    let referenceYear = +urlParams.get('referenceYear');
    let isEdit = !!membershipPrintId;
    let isPressed = false;
    let downloadPrintPromise = null;
    let initialMemberships = [];

    const { form, handleChange, handleSubmit, isValid, errors } = createForm({
        initialValues: {
            code: "",
            date: new Date(),
            notes: null,
            memberships: [],
            referenceYear: 0
        },
        validationSchema: yup.object().shape({
            code: yup.string().required("Inserisci un codice"),
            date: yup.date().required("Inserisci una data"),
            notes: yup.string().notRequired(),
            memberships: yup.array(),
            referenceYear: yup.number()
        }),
        onSubmit: (mpForm) => {
            isPressed = true;

            return isEdit ? edit(mpForm) : add(mpForm);
        },
    });

    async function edit(mp) {
        updateMembershipPrint(fromFormToPayload(mp, initialMemberships), membershipPrintId).then(() => {
            isPressed = false;
            toast.success("Stampa modificata");
            navigate("../", { replace: true });
        }).catch((err) => {
            isPressed = false;

            if(err?.response?.status === 500) {
                toast.error(`Impossibile modificare la stampa, errore ${err.toString()}`);
            }
        })
    }

    async function add(mp) {
        createMembershipPrint(fromFormToPayload(mp, initialMemberships)).then((resp) => {
            isPressed = false;
            if(resp.status === 200 && resp?.data) {
                toast.success("Stampa creata");
                navigate("/admin/prints/", { replace: true });
            }
        }).catch((err) => {
            isPressed = false;

            if(err?.response?.status === 500) {
                toast.error(`Impossibile creare la stampa, errore ${err.toString()}`);
            }
        })
    }

    async function deleteById(mp) {
        deleteMembershipPrint(mp).then(() => {
            toast.success("Stampa eliminata");
            navigate("/admin/prints/", { replace: true });
        }).catch((err) => {
            isPressed = false;

            if(err?.response?.status === 500) {
                toast.error(`Impossibile eliminare la stampa, errore ${err.toString()}`);
            }
        })
    }

    async function downloadMembershipPrintFile() {
        downloadPrintPromise = (async () => {
            try {
                const res = await exportMPToXLSX(membershipPrintId);
                const fileName = `iscrizioni_${format(new Date(), "dd_MM_yyyy")}.xlsx`;
                downloadFile(document, res.data, fileName);

                toast.success("Esportazione completata");
            } catch (err) {
                if(err?.response?.status === 500) {
                    toast.error(`Impossibile esportare le iscrizioni, errore ${err.toString()}`);
                }
            }
            finally {
                downloadPrintPromise = null;
            }
        })()
    }

    function setFormValue(mp) {
        initialMemberships = mp.memberships;

        $form = {
            ...mp,
            referenceYear: +mp.code.split("/")[0]
        }
    }

    function removeMembershipAt(index) {
        $form.memberships = $form.memberships.toSpliced(index, 1);
    }

    function openSelectMemberships() {
        openModal(SelectSubscriberModal, {
            disabledSubscribers: $form.memberships.map((mb) => mb.subscriber.id),
            referenceYear: +$form.referenceYear,
            onConfirm: (subscribers) => {
                $form.memberships = [
                    ...$form.memberships,
                    ...subscribers.map(s => {
                        const { memberships, ...sb } = s;
                        return {
                            ...memberships[0],
                            subscriber: sb
                        }
                    })
                ];

                closeAllModals()
            }
        })
    }

    onMount(async () => {
        if (!isEdit) {
            const currentYear = referenceYear ?? $selectedActiveYear;

            await getNextCode(currentYear).then((res) => {
                $form = {
                    ...$form,
                    code: res.data.code,
                    referenceYear: currentYear
                }
            }).catch((err) => {
                toast.error(`Non è possibile generare il codice della prossima stampa, err: ${err.toString()}`)
                navigate("/admin/prints/");
            });

            return;
        }

        getMembershipPrint(membershipPrintId).then((data) => {
            if (data?.status === 200) {
                setFormValue(data.data);
            }
        }).catch((err) => {
            if(err?.response?.data?.status === 404 || err?.response?.data?.status === 500) {
                toast.error("Stampa non trovata");
                navigate("/admin/prints/")
                return;
            }
            defaultError();
        });
    });
</script>

<FormCard
    subject="la stampa"
    isEdit={isEdit}
    isButtonDisabled={isPressed || !$isValid}
    handleSubmit={handleSubmit}
    handleDelete={() => deleteById(membershipPrintId)}
>
    <div slot="header-content" class="w-full">
        {#if (isEdit)}
            <ButtonPromise
                slot="header-content"
                promise="{downloadPrintPromise}"
                handleClick="{() => downloadMembershipPrintFile()}"
                content="Stampa"
                buttonClass="w-full md:w-44 bg-lightBlue-400"
            />
        {/if}
    </div>

    <div class="w-full flex flex-col gap-3 md:flex-row">
        <InputField
            containerClass="w-full md:w-6/12"
            fieldName="Anno della stampa"
            name="year"
            id="year"
            isDisabled="{true}"
            handleChange={handleChange}
            bind:value={$form.referenceYear}
        />

        <InputField
            containerClass="w-full md:w-6/12"
            fieldName="Codice della stampa"
            placeholder="Codice identificativo della stampa"
            isRequired={false}
            name="code"
            id="code"
            isDisabled="{true}"
            bind:errorText={$errors.code}
            bind:value={$form.code}
            handleChange={handleChange}
        />
    </div>

    <DateSearcher
        fieldName="Data della stampa"
        name="date"
        placeholder="Inserisci la data di stampa (gg/mm/aaaa)"
        bind:value={$form.date}
        bind:errorText={$errors.date}
    />

    <TextareaField
        fieldName="Note"
        placeholder="Nota relativa alla stampa"
        name="notes"
        isRequired={false}
        handleChange={handleChange}
        bind:errorText={$errors.notes}
        bind:value={$form.notes}
    />

    <div class="flex flex-col mb-5">
        <div class="font-450 text-lg">Tessere</div>

        <div class="flex flex-col gap-2">
            <div class="block w-full overflow-x-auto h-350-px no-scroll">
                <table class="w-full bg-transparent overscroll-auto">
                    <thead class="sticky top-0 shadow w-full z-3 bg-white h-4 overflow-x-auto">
                        <tr>
                            <th class="pl-4">Codice</th>
                            <th>Nome</th>
                            <th>Cognome</th>
                            <th>CF</th>
                            <th>EMAIL</th>
                            <th>Gruppo</th>
                            <th>Quota</th>
                            <th>UGS</th>
                            <th></th>
                        </tr>
                    </thead>

                    <tbody class="font-normal">
                        {#if (!!$form.memberships.length)}
                            {#each $form.memberships as mb, index}
                                <tr>
                                    <td class="w-6 pl-4">{mb.code}</td>
                                    <td class="w-12">{mb.subscriber.firstName}</td>
                                    <td class="w-12">{mb.subscriber.lastName}</td>
                                    <td class="w-12">{mb.subscriber.fiscalCode}</td>
                                    <td class="w-12">{mb.subscriber.email}</td>
                                    <td class="w-12">{mb.group.name}</td>
                                    <td class="w-12">{mb.paidAmount}</td>
                                    <td class="w-12">
                                        {#if mb.isUGS}
                                            <i class="fa fa-check fa-1x"></i>
                                        {/if}
                                    </td>
                                    <td class="w-10 text-right pr-4">
                                        <Tooltip maxWidth="500" content={"Rimuovi la tessera"}>
                                            <button class="w-10 h-10 text-sm text-red-500 border inline-flex items-center justify-center rounded-full cursor-pointer focus:outline-none"
                                                on:click|preventDefault={() => removeMembershipAt(index)}
                                            >
                                                <i class="fa fa-times"></i>
                                            </button>
                                        </Tooltip>
                                    </td>
                                </tr>
                            {/each}
                        {:else}
                            <td class="font-semibold text-center" style="height: 10rem" colspan={8}>
                                Non ci sono tessere
                            </td>
                        {/if}
                    </tbody>
                </table>
            </div>

            <div class="flex justify-center">
                <button
                    class="flex justify-center items-center gap-2 border-0 bg-lightBlue-400 p-2 px-4
                     font-semibold text-white transition hover:shadow-lg w-52 outline-none focus:outline-none"
                    on:click|preventDefault={openSelectMemberships}
                >
                    <i class="fa fa-plus"></i>

                    Aggiungi tessere
                </button>
            </div>
        </div>
    </div>
</FormCard>